import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { API_CONFIG } from "@/src/api/apiConfig";
import type { ApiErrorResponseDto, ApiRequestConfig } from "@/src/types/api.types";
import { rejectAxiosResponseError } from "@/src/api/axiosResponseError";
import { decrementApiLoading, incrementApiLoading } from "@/src/api/apiLoading";
import { AUTH_ENDPOINTS } from "@/src/api/endpoints";
import type { ApiResponseDto } from "@/src/types/api.types";
import type { AuthTokens } from "@/src/types/auth.types";

type AuthTokenGetter = () => string | null;
type UnauthorizedHandler = () => void;
type SessionTokensHandler = (tokens: { accessToken: string; refreshToken: string }) => void;

let getAccessToken: AuthTokenGetter = () => null;
let getRefreshToken: AuthTokenGetter = () => null;
let onUnauthorized: UnauthorizedHandler = () => undefined;
let onSessionTokens: SessionTokensHandler = () => undefined;
let latestAccessToken: string | null = null;
let refreshInFlight: Promise<{ accessToken: string; refreshToken: string } | null> | null = null;

async function refreshSessionTokens(refreshToken: string) {
  if (!refreshInFlight) {
    refreshInFlight = axiosInstance
      .post<ApiResponseDto<AuthTokens>>(
        AUTH_ENDPOINTS.REFRESH,
        { refreshToken },
        {
          skipAuth: true,
          skipRefresh: true,
          skipGlobalErrorHandler: true,
        } as InternalAxiosRequestConfig & ApiRequestConfig,
      )
      .then((response) => {
        const data = response.data?.data;
        if (!data?.accessToken || !data?.refreshToken) return null;
        return {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        };
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

export function registerAuthHandlers(handlers: {
  getAccessToken: AuthTokenGetter;
  getRefreshToken: AuthTokenGetter;
  onUnauthorized: UnauthorizedHandler;
  onSessionTokens: SessionTokensHandler;
}): void {
  getAccessToken = handlers.getAccessToken;
  getRefreshToken = handlers.getRefreshToken;
  onUnauthorized = handlers.onUnauthorized;
  onSessionTokens = handlers.onSessionTokens;
  const storedAccessToken = handlers.getAccessToken();
  if (!storedAccessToken) {
    latestAccessToken = null;
  } else if (latestAccessToken && latestAccessToken !== storedAccessToken) {
    latestAccessToken = storedAccessToken;
  }
}

function resolveAccessToken(): string | null {
  const stored = getAccessToken();
  if (!stored) {
    latestAccessToken = null;
    return null;
  }
  return latestAccessToken ?? stored;
}

function isRefreshRequest(url: string | undefined): boolean {
  return Boolean(url?.includes(AUTH_ENDPOINTS.REFRESH));
}

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeoutMs,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig & ApiRequestConfig) => {
  if (!config.skipGlobalErrorHandler) {
    incrementApiLoading();
  }
  const method = config.method?.toLowerCase();
  if ((method === "get" || method === "head") && config.data === undefined) {
    delete config.headers["Content-Type"];
  }
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  const headerToken = config.tokenHeader ?? (!config.skipAuth ? resolveAccessToken() : null);
  if (config.tokenHeader) {
    config.headers.token = config.tokenHeader;
  }
  if (headerToken && !config.skipAuth) {
    config.headers.Authorization = `${API_CONFIG.authHeaderPrefix} ${headerToken}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    const cfg = response.config as ApiRequestConfig | undefined;
    if (!cfg?.skipGlobalErrorHandler) {
      decrementApiLoading();
    }
    return response;
  },
  async (error: AxiosError<ApiErrorResponseDto>) => {
    const cfg = error.config as (InternalAxiosRequestConfig & ApiRequestConfig & { _retry?: boolean }) | undefined;
    if (!cfg?.skipGlobalErrorHandler) {
      decrementApiLoading();
    }

    const shouldTryRefresh =
      error.response?.status === 401 &&
      cfg &&
      !cfg.skipAuth &&
      !cfg.skipRefresh &&
      !cfg._retry &&
      !isRefreshRequest(cfg.url);

    if (shouldTryRefresh) {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const tokens = await refreshSessionTokens(refreshToken);
        if (tokens) {
          latestAccessToken = tokens.accessToken;
          onSessionTokens(tokens);
          cfg._retry = true;
          cfg.headers.Authorization = `${API_CONFIG.authHeaderPrefix} ${tokens.accessToken}`;
          return axiosInstance.request(cfg);
        }
      }
    }

    return rejectAxiosResponseError(error, { onUnauthorized });
  },
);
